/**
 * ライセンス管理（Excel）画面のセル色・コメント情報および変換ユーティリティ
 *
 * 各ライセンス（行）は cellMeta という JSON 文字列カラムを持ち、以下の形式でセル単位の
 * 背景色・コメントを保持する:
 *
 *   { "<元の列インデックス>": { "color"?: string, "comment"?: string } }
 *
 * 列インデックスは COLUMNS 配列（ライセンス管理（Excel）画面）の固定順（Orig_Index）を指す。
 * 表示上の列並び替え（columnOrder / Display_Order）とは独立して保存されるため、
 * ユーザーが列順を変更してもセルの色・コメントは正しい項目に紐付いたまま残る。
 *
 * 既存の `assetCellMeta.ts` / `contractCellMeta.ts` は既存2画面が使用中のため変更せず、
 * ライセンス画面用に本ファイルを新設している。あわせて、画面コンポーネント内に private
 * 関数として置くとテストできない変換ヘルパー（日付・数値変換、Display_Order ⇄ Orig_Index
 * の相互変換、列状態の永続化）も、単体テスト（property-based test）の対象にできるよう
 * ここへ集約している。
 */

export interface CellMetaEntry {
  color?: string;
  comment?: string;
}

export type CellMetaMap = Record<number, CellMetaEntry>;

/** 選択可能な背景色パレット（右クリックメニューの「セル色変更」用） */
export const CELL_COLOR_PALETTE: { label: string; value: string }[] = [
  { label: '赤', value: '#ffcdd2' },
  { label: '黄', value: '#fff9c4' },
  { label: '緑', value: '#c8e6c9' },
  { label: '青', value: '#bbdefb' },
  { label: 'グレー', value: '#e0e0e0' },
  { label: 'オレンジ', value: '#ffe0b2' },
  { label: '紫', value: '#e1bee7' },
  { label: '水色', value: '#b2ebf2' },
  { label: 'ピンク', value: '#f8bbd0' },
  { label: '黄緑', value: '#dcedc8' },
];

export function parseCellMeta(raw: string | null | undefined): CellMetaMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as CellMetaMap;
    }
  } catch {
    // ignore malformed JSON, treat as empty
  }
  return {};
}

export function serializeCellMeta(meta: CellMetaMap): string | null {
  const cleaned: CellMetaMap = {};
  for (const [key, entry] of Object.entries(meta)) {
    if (entry && (entry.color || entry.comment)) {
      cleaned[Number(key)] = entry;
    }
  }
  return Object.keys(cleaned).length > 0 ? JSON.stringify(cleaned) : null;
}

export function setCellColor(meta: CellMetaMap, colIdx: number, color: string | null): CellMetaMap {
  const next = { ...meta };
  const entry = { ...(next[colIdx] || {}) };
  if (color) {
    entry.color = color;
  } else {
    delete entry.color;
  }
  if (entry.color || entry.comment) {
    next[colIdx] = entry;
  } else {
    delete next[colIdx];
  }
  return next;
}

export function setCellComment(meta: CellMetaMap, colIdx: number, comment: string | null): CellMetaMap {
  const next = { ...meta };
  const entry = { ...(next[colIdx] || {}) };
  if (comment && comment.trim()) {
    entry.comment = comment.trim();
  } else {
    delete entry.comment;
  }
  if (entry.color || entry.comment) {
    next[colIdx] = entry;
  } else {
    delete next[colIdx];
  }
  return next;
}

// ============================================================
// Spreadsheet cell-name <-> coordinate helpers
//
// Jspreadsheet CE (jsuites) internally represents cells using spreadsheet-style
// names such as "A1", "B12", etc. Rather than depending on the library's
// undocumented static helpers (jexcel.getColumnNameFromId / getIdFromColumnName),
// we reimplement the same well-defined algorithm here so this logic is
// self-contained and doesn't break if the library's internals change.
// ============================================================

/** 0-indexed column + 0-indexed row -> spreadsheet cell name (e.g. [0,0] -> "A1"). */
export function cellNameFromCoords(colIdx: number, rowIdx: number): string {
  let col = colIdx;
  let letters = '';
  if (col > 701) {
    letters += String.fromCharCode(64 + Math.floor(col / 676));
    letters += String.fromCharCode(64 + Math.floor((col % 676) / 26));
  } else if (col > 25) {
    letters += String.fromCharCode(64 + Math.floor(col / 26));
  }
  letters += String.fromCharCode(65 + (col % 26));
  return `${letters}${rowIdx + 1}`;
}

/** Spreadsheet cell name (e.g. "B12") -> 0-indexed [col, row]. Returns [0,0] if unparsable. */
export function coordsFromCellName(cellName: string): [number, number] {
  const match = /^([A-Za-z]+)(\d+)$/.exec(cellName);
  if (!match) return [0, 0];
  const letters = match[1].toUpperCase();
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col += (letters.charCodeAt(i) - 64) * Math.pow(26, letters.length - 1 - i);
  }
  col -= 1;
  if (col < 0) col = 0;
  const row = Math.max(0, parseInt(match[2], 10) - 1);
  return [col, row];
}

/** Extracts the `background-color` value from a CSS style string like "background-color:#fff;font-weight:bold". */
export function extractBackgroundColor(styleStr: string | null | undefined): string | null {
  if (!styleStr) return null;
  const match = /background-color\s*:\s*([^;]+)/i.exec(styleStr);
  return match ? match[1].trim() : null;
}

// ============================================================
// Value conversion helpers (grid -> API payload)
// ============================================================

/**
 * カレンダー列の表示値（`options.format: 'YYYY/MM/DD'`）を、サーバーの Zod バリデータが
 * 要求する `'YYYY-MM-DD'` 文字列へ変換する。スラッシュ区切りのまま送ると
 * 「日付はYYYY-MM-DD形式で入力してください」で 400 になるため必須の変換。
 * 月・日が1桁の `'YYYY/M/D'` も受け付けて 0 埋めする。空値は null。
 */
export function toIsoDateOrNull(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (raw === '') return null;
  const match = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(raw);
  if (match) {
    const [, y, m, d] = match;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  // Already in YYYY-MM-DD (or another format the server will validate/reject
  // on its own) — pass through unchanged.
  return raw;
}

/**
 * セル値を保存用の数値へ変換する。マスク付き数値列は `"1,000"` のようなカンマ区切り
 * 文字列で返ってくるためカンマを除去してから数値化する。
 * 空値・空白のみ・数値化できない値は null（＝DB では NULL）。
 */
export function toNumberOrNull(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).replace(/,/g, '').trim();
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

// ============================================================
// Display_Order <-> Orig_Index mapping
//
// columnOrderRef.current = order   // order[displayIdx] = origIdx
// ============================================================

/** Display_Order 行 → Orig_Index 行。`order[displayIdx] = origIdx`。 */
export function displayToOriginalRow<T>(displayRow: T[], order: number[], columnCount: number): (T | null)[] {
  const originalRow: (T | null)[] = Array(columnCount).fill(null);
  for (let displayIdx = 0; displayIdx < order.length; displayIdx++) {
    const origIdx = order[displayIdx];
    if (origIdx >= 0 && origIdx < columnCount) originalRow[origIdx] = displayRow[displayIdx];
  }
  return originalRow;
}

/** Orig_Index 行 → Display_Order 行。 */
export function originalToDisplayRow<T>(originalRow: T[], order: number[]): T[] {
  return order.map((origIdx) => originalRow[origIdx]);
}

/** 固定列を必ず先頭に置いた列順へ正規化する（保存済み order の読み込み直後に必ず通す）。 */
export function normalizeColumnOrder(order: number[], fixed: number[]): number[] {
  return [...fixed, ...order.filter((idx) => !fixed.includes(idx))];
}

// ============================================================
// Column state persistence (localStorage, per user)
// ============================================================

export interface ColumnSavedState {
  widths: number[];
  order: number[];
  /** Original column indices that are currently hidden (per-user persisted). */
  hidden?: number[];
}

export function getColumnStateKey(userId: string): string {
  return `license-excel-col-state-${userId}`;
}

/** 列状態を LocalStorage に保存する。書き込み失敗（容量超過・プライベートモード等）は握りつぶす。 */
export function saveColumnState(userId: string, state: ColumnSavedState): void {
  try {
    localStorage.setItem(getColumnStateKey(userId), JSON.stringify(state));
  } catch {
    // ignore storage failures; the grid keeps working with in-memory state
  }
}

/** 列状態を LocalStorage から復元する。未保存・不正 JSON の場合は null。 */
export function loadColumnState(userId: string): ColumnSavedState | null {
  try {
    const stored = localStorage.getItem(getColumnStateKey(userId));
    return stored ? (JSON.parse(stored) as ColumnSavedState) : null;
  } catch {
    return null;
  }
}

/** 非表示列集合から固定列を除外する（固定列は常に表示状態を維持する）。 */
export function sanitizeHiddenColumns(hidden: Iterable<number> | null | undefined, fixed: number[]): Set<number> {
  const result = new Set<number>();
  if (!hidden) return result;
  for (const idx of hidden) {
    if (!fixed.includes(idx)) result.add(idx);
  }
  return result;
}
